import { useState } from "react";
import { CheckCircle, XCircle, Brain, ChevronRight, RotateCcw } from "lucide-react";

const QuizPanel = ({ lesson, progress, onComplete }) => {
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [score, setScore] = useState(0);

  const quiz = lesson?.quiz || [];
  const alreadyCompleted = progress?.quiz_completed;

  const handleSelect = (qIndex, optIndex) => {
    if (submitted || alreadyCompleted) return;
    setAnswers({ ...answers, [qIndex]: optIndex });
  };

  const handleSubmit = () => {
    let correct = 0;
    quiz.forEach((q, i) => {
      if (answers[i] === q.answer) correct++;
    });
    setScore(correct);
    setSubmitted(true);
    onComplete(correct, quiz.length);
  };

  const handleRetry = () => {
    setAnswers({});
    setSubmitted(false);
    setScore(0);
  };

  const allAnswered = Object.keys(answers).length === quiz.length;

  if (alreadyCompleted) {
    return (
      <div data-testid="quiz-completed" className="rounded-2xl p-8 text-center" style={{ background: "white", border: "1px solid var(--ll-border)" }}>
        <div className="w-16 h-16 rounded-full mx-auto flex items-center justify-center mb-4" style={{ background: "var(--ll-green)" }}>
          <CheckCircle size={28} style={{ color: "white" }} />
        </div>
        <h3 className="font-serif text-xl font-medium mb-2" style={{ color: "var(--ll-text)" }}>Quiz Completed</h3>
        <p className="text-sm mb-1" style={{ color: "var(--ll-text-secondary)", fontFamily: "'Outfit', sans-serif" }}>
          You scored {progress.quiz_score}/{progress.quiz_total}
        </p>
        <div className="flex justify-center gap-1 mt-3">
          {Array.from({ length: progress.quiz_total }).map((_, i) => (
            <div key={`score-dot-${i}`} className="w-3 h-3 rounded-full" style={{ background: i < progress.quiz_score ? "var(--ll-green)" : "var(--ll-border)" }} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div data-testid="quiz-panel" className="space-y-6">
      <div className="flex items-center gap-3 mb-2">
        <Brain size={20} style={{ color: "var(--ll-accent)" }} />
        <div>
          <h3 className="font-serif text-xl font-medium" style={{ color: "var(--ll-text)" }}>Knowledge Check</h3>
          <p className="text-xs" style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}>
            {quiz.length} questions — test your understanding of {lesson.title}
          </p>
        </div>
      </div>

      {quiz.map((q, qIndex) => {
        const userAnswer = answers[qIndex];
        const isCorrect = submitted && userAnswer === q.answer;
        const isWrong = submitted && userAnswer !== undefined && userAnswer !== q.answer;

        return (
          <div key={`q-${qIndex}-${q.q.slice(0,20)}`} data-testid={`quiz-question-${qIndex}`} className="rounded-2xl p-6" style={{ background: "white", border: `1px solid ${submitted ? (isCorrect ? "var(--ll-green)" : isWrong ? "#e87c7c" : "var(--ll-border)") : "var(--ll-border)"}` }}>
            <div className="flex items-start gap-3 mb-4">
              <span className="text-xs font-medium w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                style={{ background: "var(--ll-bg-secondary)", color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}>
                {qIndex + 1}
              </span>
              <p className="text-sm font-medium" style={{ color: "var(--ll-text)", fontFamily: "'Outfit', sans-serif" }}>{q.q}</p>
            </div>

            <div className="space-y-2 ml-9">
              {q.options.map((opt, optIndex) => {
                const isSelected = userAnswer === optIndex;
                const showCorrect = submitted && optIndex === q.answer;
                const showWrong = submitted && isSelected && optIndex !== q.answer;

                return (
                  <button key={`opt-${qIndex}-${optIndex}-${opt.slice(0,10)}`} data-testid={`quiz-option-${qIndex}-${optIndex}`}
                    onClick={() => handleSelect(qIndex, optIndex)}
                    disabled={submitted}
                    className="w-full text-left flex items-center gap-3 p-3 rounded-xl transition-all duration-300"
                    style={{
                      background: showCorrect ? "rgba(163,184,153,0.15)" : showWrong ? "rgba(232,124,124,0.1)" : isSelected ? "var(--ll-bg-secondary)" : "transparent",
                      border: `1px solid ${showCorrect ? "var(--ll-green)" : showWrong ? "#e87c7c" : isSelected ? "var(--ll-text-muted)" : "var(--ll-border)"}`,
                      cursor: submitted ? "default" : "pointer",
                    }}>
                    <div className="w-5 h-5 rounded-full border flex items-center justify-center flex-shrink-0"
                      style={{ borderColor: showCorrect ? "var(--ll-green)" : showWrong ? "#e87c7c" : isSelected ? "var(--ll-text)" : "var(--ll-border)" }}>
                      {showCorrect && <CheckCircle size={12} style={{ color: "var(--ll-green)" }} />}
                      {showWrong && <XCircle size={12} style={{ color: "#e87c7c" }} />}
                      {!submitted && isSelected && <div className="w-2.5 h-2.5 rounded-full" style={{ background: "var(--ll-text)" }} />}
                    </div>
                    <span className="text-sm" style={{ color: "var(--ll-text-secondary)", fontFamily: "'Outfit', sans-serif" }}>{opt}</span>
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* Submit / Results */}
      {!submitted ? (
        <button data-testid="quiz-submit-btn" onClick={handleSubmit} disabled={!allAnswered}
          className="w-full rounded-full px-6 py-3.5 text-sm font-medium transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg flex items-center justify-center gap-2 disabled:opacity-40"
          style={{ background: "var(--ll-cta)", color: "var(--ll-cta-text)", fontFamily: "'Outfit', sans-serif" }}>
          Submit Answers <ChevronRight size={14} />
        </button>
      ) : (
        <div className="rounded-2xl p-6 text-center" style={{ background: score === quiz.length ? "rgba(163,184,153,0.1)" : "rgba(247,208,70,0.08)", border: "1px solid var(--ll-border)" }}>
          <p className="font-serif text-2xl font-medium mb-1" style={{ color: "var(--ll-text)" }}>
            {score}/{quiz.length} Correct
          </p>
          <p className="text-sm mb-4" style={{ color: "var(--ll-text-secondary)", fontFamily: "'Outfit', sans-serif" }}>
            {score === quiz.length ? "Perfect score! You've mastered this lesson." : score >= quiz.length / 2 ? "Good work! Review the questions you missed." : "Keep learning — rewatch the video and try again."}
          </p>
          {score < quiz.length && (
            <button onClick={handleRetry} className="text-xs font-medium flex items-center gap-1 mx-auto transition-colors hover:opacity-70" style={{ color: "var(--ll-text-secondary)", fontFamily: "'Outfit', sans-serif" }}>
              <RotateCcw size={12} /> Try Again
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default QuizPanel;
